import _ from 'lodash';

// eslint-disable-next-line import/no-cycle,import/no-unresolved
import { getProductWeight } from 'shared/helpers/products';
// eslint-disable-next-line import/no-cycle
import {
  matchesBrand,
  matchesCategory,
  matchesInventoryTag,
  matchesProductId,
  matchesProductTag,
  matchesStrain,
  matchesVendor,
} from 'shared/order/bogo/common';

/* **************************
 * A set of helpers specifically for 3.5 specials
 *************************** */

// Accepts a product, a set of restrictions (bogoConditions, bogoRewards, saleDiscounts), and a special object
// Returns an object with a boolean for whether any option was found, along with the eligible set per restriction
export function checkProductForEligibleOptions(product = {}, restrictions = [], special = {}) {
  const eligibleProductOptionsPerRestriction = _.map(restrictions, (restriction) =>
    eligibleProductOptionsForRestriction(product, restriction, 'inclusion', special)
  );
  let productHasEligibleOption = false;

  _.forEach(eligibleProductOptionsPerRestriction, (options) => {
    if (options.length > 0) {
      productHasEligibleOption = true;
      return false;
    }
    return true;
  });

  return { productHasEligibleOption, eligibleProductOptionsPerRestriction };
}

// Accepts a special. Returns true or false depending on if the special is v3.5
export function isSpecialVersion3dot5(special = {}) {
  return (
    (special?.version === 3 && special?.minorVersion === 5) ||
    // TODO: remove this once minorVersion is being added by Arma discountSync as 5
    isArmageddonDiscountSyncSpecial(special)
  );
}

// Accepts a special. Returns true or false depending on if the special is created or updated by discount sync v2
export function isArmageddonDiscountSyncSpecial(special = {}) {
  return special?.createdBy === 'ArmageddonDiscountSync' || special?.updatedBy === 'ArmageddonDiscountSync';
}

// Accepts a product, a restriction object (bogoCondition, bogoRewards, saleDiscount, or an exclusion),
//    a restrictionType ('inclusion' or 'exclusion'), and a special object
// Returns an array of all eligible product options (weights) for that restriction
export function eligibleProductOptionsForRestriction(
  product = {},
  restriction = {},
  restrictionType = 'inclusion',
  special = {}
) {
  // Guard against null restrictions (default param only covers undefined)
  if (!restriction) {
    return [];
  }

  // Totally skip the restriction if 'IGNORE' is present
  if (restriction.productIds?.[0] === 'IGNORE') {
    return [];
  }

  return eligibleProductOptionsV2(product, restriction, restrictionType, special);
}

/* ******************************************
 * Implementation helpers
 ****************************************** */
function eligibleProductOptionsV2(product, restriction, restrictionType, special) {
  // For exclusions, just find matching options
  if (restrictionType === 'exclusion') {
    return findMatchingOptionsV2(product, restriction, special);
  }

  // For inclusions, find eligible options then subtract exclusions
  const baseOptions = findMatchingOptionsV2(product, restriction, special);

  // Handle nested exclusions
  if (!_.isEmpty(restriction.exclusions)) {
    const excludedOptions = findExcludedOptionsV2(product, restriction.exclusions, special);
    return _.difference(baseOptions, excludedOptions);
  }

  return baseOptions;
}

// Find all options that match the restriction criteria
function findMatchingOptionsV2(product, restriction, special) {
  const matchOperator = getMatchOperator(restriction, 'inclusion');

  // Check if product matches non-weight criteria
  const { isMatch: productMatches, isNonProductIdMatch } = matchesRestriction(
    product,
    matchOperator === 'or' && special?.directPOSProductIdMapping ? { ...restriction, productIds: [] } : restriction,
    'inclusion'
  );

  if (!productMatches && matchOperator === 'and') {
    return [];
  }

  // Get weight-filtered options (this is where bug fixes live)
  return getWeightFilteredOptionsV2(product, restriction, matchOperator, special, productMatches, isNonProductIdMatch);
}

// Process nested exclusions (non-recursive, just maps)
function findExcludedOptionsV2(product, exclusions, special) {
  return _.union(...exclusions.map((exclusion) => findMatchingOptionsV2(product, exclusion, special)));
}

// Get options filtered by weight criteria with bug fixes
function getWeightFilteredOptionsV2(product, restriction, matchOperator, special, productMatches, isNonProductIdMatch) {
  let eligibleOptions = [];

  // For 'or' cases, get weight options upfront
  if (matchOperator === 'or') {
    eligibleOptions = getMatchingWeightOptionsV2(product, restriction, true, matchOperator, special);
  }

  // If product matches, get the appropriate weight options
  if (productMatches || eligibleOptions.length > 0) {
    if (matchOperator === 'and') {
      eligibleOptions = getMatchingWeightOptionsV2(product, restriction, false, matchOperator, special);
    } else if (productMatches) {
      // For 'or' cases with a match, get all weight options
      const adjustedRestriction = {
        productIds: restriction?.productIds ?? [],
        weights: [],
        inventoryTags: restriction?.inventoryTags ?? [],
      };
      if (isNonProductIdMatch && special?.directPOSProductIdMapping) {
        adjustedRestriction.productIds = [];
      }
      eligibleOptions = getMatchingWeightOptionsV2(product, adjustedRestriction, false, matchOperator, special);
    }

    // Handle activeBatchTags format
    if (!_.isArray(eligibleOptions)) {
      eligibleOptions = _.uniq([...eligibleOptions.filteredOptions, ...eligibleOptions.filteredRawOptions]);
    }

    return eligibleOptions;
  }

  return [];
}

function getMatchingWeightOptionsV2(product, restriction, returnEmptyIfNoCriteria, matchOperator, special) {
  const { weights = [], weightOperator = 'equalTo' } = restriction;
  const weightConstraints = _.compact(weights);
  const activeBatchTagsConstraints = restriction.inventoryTags ?? [];
  let productOptions = [];
  let rawProductOptions = [];

  // Get product options
  if (!_.isEmpty(product.Options)) {
    productOptions = [...product.Options];
  } else if (product.option) {
    productOptions.push(product.option);
  }

  if (special?.rawPOSWeightMapping) {
    if (!_.isEmpty(product.rawOptions)) {
      rawProductOptions = [...product.rawOptions];
    } else if (product.rawOption) {
      rawProductOptions.push(product.rawOption);
    }
  }

  if (
    (_.isEmpty(weightConstraints) || _.includes(weightConstraints, 'Any Weight')) &&
    _.isEmpty(activeBatchTagsConstraints)
  ) {
    const optionsWhenNoCriteria = returnEmptyIfNoCriteria ? [] : productOptions;

    if (special?.directPOSProductIdMapping && !_.isEmpty(restriction.productIds)) {
      return getOptionsFromPOSProductIds(product, optionsWhenNoCriteria, restriction, special);
    }
    return optionsWhenNoCriteria;
  }

  // Filter by activeBatchTags if present
  if (!_.isEmpty(activeBatchTagsConstraints)) {
    const { filteredOptions, filteredRawOptions } = getOptionsFromActiveBatchTags(
      product,
      productOptions,
      rawProductOptions,
      restriction
    );
    if (_.isEmpty(weightConstraints)) {
      return { filteredOptions, filteredRawOptions };
    }
    productOptions = filteredOptions;
    rawProductOptions = filteredRawOptions;
  }

  // Filter by weight criteria
  const checkEqualTo =
    _.isNil(weightOperator) || weightOperator === 'equalTo' || weightOperator === 'greaterThanEqualTo';
  const checkGreaterThan = weightOperator === 'greaterThan' || weightOperator === 'greaterThanEqualTo';
  const weightConstraintsInGrams = _.map(weightConstraints, (constraint) => getProductWeight(constraint));

  const weightFilteredOptions = _.reduce(
    productOptions,
    (matchingProductOptions, productOption, optionIndex) => {
      const productOptionInGrams = getProductWeight(productOption);
      const rawProductOption = rawProductOptions[optionIndex] ?? null;
      const rawProductOptionInGrams = rawProductOption ? getProductWeight(rawProductOptions[optionIndex]) : null;

      _.forEach(weightConstraintsInGrams, (weightConstraintInGrams, index) => {
        if (
          checkEqualTo &&
          (productOption === weightConstraints[index] ||
            productOptionInGrams === weightConstraintInGrams ||
            rawProductOption === weightConstraints[index] ||
            rawProductOptionInGrams === weightConstraintInGrams ||
            weightConstraints[index] === 'Any Weight')
        ) {
          matchingProductOptions.push(productOption);
          return false;
        }
        if (
          checkGreaterThan &&
          (productOptionInGrams > weightConstraintInGrams || rawProductOptionInGrams > weightConstraintInGrams)
        ) {
          matchingProductOptions.push(productOption);
          return false;
        }
        return true;
      });

      return matchingProductOptions;
    },
    []
  );

  if (special?.directPOSProductIdMapping && !_.isEmpty(restriction.productIds)) {
    const posProductIdFilteredOptions = getOptionsFromPOSProductIds(product, productOptions, restriction, special);
    const combinedFilteredOptions =
      matchOperator === 'or'
        ? _.union(posProductIdFilteredOptions, weightFilteredOptions)
        : _.intersection(posProductIdFilteredOptions, weightFilteredOptions);
    return _.intersection(productOptions, combinedFilteredOptions);
  }

  return weightFilteredOptions;
}

// Accepts a product, restriction (bogoCondition, bogoReward, saleDiscount, or an exclusion), and a restrictionType (inclusion or exclusion)
// Returns an object with booleans indicating whether the product matches restriction criteria on productIds or non-productIds fields
function matchesRestriction(product = {}, restriction = {}, restrictionType = 'inclusion') {
  if (restriction.productGroup === 'all') {
    return { isMatch: true, isNonProductIdMatch: true };
  }

  const matchOperator = getMatchOperator(restriction, restrictionType);

  // For 'or' cases, we should only be returning true if criteria is present and a match
  // For 'and' cases, if criteria is not present it is still considered a match
  const failIfNoCriteria = matchOperator === 'or';

  if (matchOperator === 'or') {
    if (
      matchesBrand(restriction, product, 'brand', failIfNoCriteria) ||
      matchesCategory(restriction, product, 'category', failIfNoCriteria) ||
      matchesStrain(restriction, product, failIfNoCriteria) ||
      matchesVendor(restriction, product, failIfNoCriteria) ||
      matchesInventoryTag(restriction, product, failIfNoCriteria) ||
      matchesProductTag(restriction, product, failIfNoCriteria)
    ) {
      return { isMatch: true, isNonProductIdMatch: true };
    }

    if (matchesProductId(restriction, product, failIfNoCriteria)) {
      return { isMatch: true, isNonProductIdMatch: false };
    }

    return { isMatch: false, isNonProductIdMatch: false };
  }

  const brandMatch = matchesBrand(restriction, product, 'brand', failIfNoCriteria);
  const categoryMatch = matchesCategory(restriction, product, 'category', failIfNoCriteria);
  const productMatch = matchesProductId(restriction, product, failIfNoCriteria);
  const strainMatch = matchesStrain(restriction, product, failIfNoCriteria);
  const vendorMatch = matchesVendor(restriction, product, failIfNoCriteria);
  const productTagMatch = matchesProductTag(restriction, product, failIfNoCriteria);
  const inventoryTagMatch = matchesInventoryTag(restriction, product, failIfNoCriteria);

  const nonProductMatch =
    brandMatch && categoryMatch && strainMatch && vendorMatch && productTagMatch && inventoryTagMatch;
  return {
    isMatch: productMatch && nonProductMatch,
    isNonProductIdMatch: nonProductMatch,
  };
}

// Accepts a product restriction object (bogoCondition, bogoReward, saleDiscount, or an exclusion) and a restrictionType (inclusion or exclusion)
// Returns the matchOperator designating logic between product group restrictions ("and" or "or")
function getMatchOperator(restriction = {}, restrictionType = 'inclusion') {
  const defaultMatchOperator = restrictionType === 'inclusion' ? 'and' : 'or';
  return restriction?.matchOperator ? restriction.matchOperator : defaultMatchOperator;
}

// Accepts a product, set of productOptions, and restriction object
// Returns either the set of options applicable for this product based on child canonicalIDs and restriction criteria
//    or the productOptions unaltered if no productIds are present on the restriction
function getOptionsFromPOSProductIds(product = {}, productOptions = [], restriction = {}) {
  const productCriteria = [...(restriction.productIds ?? [])];
  if (_.isEmpty(productCriteria)) {
    return productOptions;
  }

  return _.reduce(
    product.POSMetaData?.children,
    (options, child) => {
      if (_.includes(productCriteria, child?.canonicalID)) {
        options.push(child.option);
      }
      return options;
    },
    []
  );
}

function getOptionsFromActiveBatchTags(product = {}, productOptions = [], rawOptions = [], restriction = {}) {
  const qualifiedTags = [...(restriction.inventoryTags ?? [])];
  if (_.isEmpty(qualifiedTags)) {
    return { productOptions, rawOptions };
  }

  const filterOptions = (options, getOptionValue) =>
    _.reduce(
      options,
      (filteredOptions, option) => {
        const childProduct = product.POSMetaData?.children?.find((child) => child.option === getOptionValue(option));
        const childTags = childProduct?.activeBatchTags?.map((tag) => tag.tagId);
        if (_.intersection(qualifiedTags, childTags).length > 0) {
          filteredOptions.push(option);
        }
        return filteredOptions;
      },
      []
    );

  const filteredOptions = filterOptions(productOptions, (option) => option);
  const filteredRawOptions = filterOptions(rawOptions, getProductWeight);

  return { filteredOptions, filteredRawOptions };
}
